import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
import { InviteWelcome } from './invite-welcome.js';
import { Quests } from './quests.js';
import { showAchievementBanner } from './banner.js';
/**
 * UI.InviteQuest — the invitation, pinned into the quest rail.
 *
 * The brief (`invite-welcome.js`) explains the invitation reward once. This is
 * where it LIVES afterwards: a special quest slot at the top of the rail
 * carrying the same numbers, so a player who closed the screen can still see
 * how many matches remain. Without it, dismissing the brief loses the only
 * statement of the condition, and the reward becomes indistinguishable from a
 * bug again the moment the overlay is gone.
 *
 * It is a projection of the server's referral state and nothing else. It grants
 * nothing, counts nothing itself, and disappears the moment the referral stops
 * being pending. A failed lookup renders no row rather than a guessed one.
 *
 * Contract: tests/unit/ui/inviteQuest.test.js
 */
let InviteQuest = {};

InviteQuest = (function () {

  const ITEM_ID = 'lobby-quest-invite';
  let cached = null;      // the last referral state the server actually returned
  let inFlight = null;

  function uiText(key, fallback, replacements) {
    try {
      const value = i18n?.t?.(key, replacements);
      if (typeof value === 'string' && value && value !== key) return value;
    } catch (_) { /* i18n not ready */ }
    if (!replacements) return fallback;
    return Object.entries(replacements).reduce(
      (text, [token, value]) => String(text)
        .split(`{{${token}}}`).join(String(value))
        .split(`{${token}}`).join(String(value)),
      String(fallback),
    );
  }

  /** Only a pending invitation has anything left to track. */
  function isTrackable(invitedBy) {
    if (!invitedBy) return false;
    if (invitedBy.status && invitedBy.status !== 'pending') return false;
    const { needed, packs } = InviteWelcome?.terms?.(invitedBy) || {};
    return Number(needed) > 0 && Number(packs) > 0;
  }

  /** The row's words — everything in it that a language can change. */
  function words(invitedBy) {
    const { needed, packs, remaining } = InviteWelcome.terms(invitedBy);
    const inviter = String(invitedBy.inviterName || '').trim();
    return {
      text: inviter
        ? uiText(
          'invite.questTextNamed', 'Invited by {{name}} — finish {{needed}} matches',
          { name: inviter, needed },
        )
        : uiText('invite.questText', 'Invitation — finish {{needed}} matches', { needed }),
      /* NAME BOTH HALVES. It advertised "+3 packs" while the payout is packs
         AND Moonlight, so the row under-sold the very reward it exists to sell. */
      reward: remaining > 0
        ? uiText('invite.questRewardFull', '+{{packs}} packs & Moonlight', { packs })
        : uiText('invite.questRewardReadyFull', '+{{packs}} packs & Moonlight incoming', { packs }),
    };
  }

  function build(invitedBy) {
    const { done, needed, remaining } = InviteWelcome.terms(invitedBy);
    const copy = words(invitedBy);

    const item = document.createElement('div');
    item.id = ITEM_ID;
    /* `lobby-quest-item` so it inherits the rail's own layout exactly; the
       modifier only changes the accent, never the geometry. */
    item.className = 'lobby-quest-item lobby-quest-item--invite';
    if (remaining <= 0) item.classList.add('lobby-quest-item--done');

    const text = document.createElement('div');
    text.className = 'lobby-quest-text';
    text.textContent = copy.text;

    const progress = document.createElement('div');
    progress.className = 'lobby-quest-progress';
    progress.textContent = `${done}/${needed}`;

    const bar = document.createElement('div');
    bar.className = 'lobby-quest-bar';
    const fill = document.createElement('div');
    fill.className = 'lobby-quest-bar-fill';
    fill.style.width = `${Math.max(0, Math.min(100, (done / needed) * 100))}%`;
    bar.appendChild(fill);

    const reward = document.createElement('div');
    reward.className = 'lobby-quest-invite-reward';
    reward.textContent = copy.reward;

    item.append(text, progress, bar, reward);
    return item;
  }

  function setText(el, value) {
    if (!el || el.textContent === value) return false;
    el.textContent = value;
    return true;
  }

  /**
   * Rewrite the row's words in the language that has since arrived — the
   * same job, for the same reason, as `ExchangeQuest.relocalize`: the row is
   * usually built before ui.json lands, and a rebuild would re-insert it under
   * the player. Only differing words are written.
   */
  function relocalize(list) {
    const target = list || document.getElementById('lobby-quests-list');
    const row = target?.querySelector?.(`#${ITEM_ID}`);
    if (!row || !isTrackable(cached)) return false;
    const copy = words(cached);
    const text = setText(row.querySelector('.lobby-quest-text'), copy.text);
    const reward = setText(row.querySelector('.lobby-quest-invite-reward'), copy.reward);
    return text || reward;
  }

  /**
   * The quest-completion beat.
   *
   * Deliberately the SAME control the ordinary quests and the pinned Moon
   * Exchange row use, so the three read as one system. It is the first of two
   * beats: this marks the quest finishing, and `StarterReward` carries the
   * payout itself — the same brief-then-grant shape the arrival ceremony uses.
   * Decoration over an authoritative fact, so a failure here must never look
   * like a failure to earn.
   */
  function celebrate(invitedBy) {
    const { packs } = InviteWelcome?.terms?.(invitedBy) || {};
    try {
      showAchievementBanner(
        uiText('invite.questCompleteTitle', 'Invitation complete!'),
        uiText('invite.questCompleteBody',
          'You both earn {{packs}} card packs and Moonlight.',
          { packs: Number(packs) > 0 ? packs : '' }),
        null,
        'invite',
        { durationMs: 5200 },
      );
    } catch (_) { /* the reward stands whether or not it is announced */ }
  }

  /** How many rail slots this occupies — the rail counts it as a real quest. */
  function count() {
    return isTrackable(cached) ? 1 : 0;
  }

  /**
   * Put the pinned row at the top of `list`, or take it away.
   *
   * `quests.js` rebuilds the list from scratch on every render, so this is
   * called AFTER each of those renders rather than once at startup.
   */
  function mount(list, { flash = false } = {}) {
    const target = list || document.getElementById('lobby-quests-list');
    if (!target) return false;
    target.querySelector(`#${ITEM_ID}`)?.remove();
    if (!isTrackable(cached)) return false;

    const item = build(cached);
    // Spent once played, or it replays whenever the rail is shown again.
    if (flash) Quests?.armTick?.(item);
    target.prepend(item);
    return true;
  }

  /**
   * Re-read the server's referral state and re-render.
   *
   * Deduplicated: the rail refresh, the brief's dismissal and a returning
   * player can all land at once, and three concurrent reads would be three
   * chances to render three different truths.
   */
  /*
   * `deferMount`: the quest rail reads this alongside its own quests and
   * renders all rows in ONE pass (quests.js), so a row landing on its own a
   * beat later can never pop into a rail the player is already reading.
   */
  function refresh({ flash = false, deferMount = false } = {}) {
    if (inFlight) return inFlight;
    const read = MoonfallCloud?.referrals?.getProgram;
    if (typeof read !== 'function') return Promise.resolve(false);

    inFlight = Promise.resolve()
      .then(() => read.call(MoonfallCloud.referrals))
      .then((result) => {
        /* CELEBRATE THE CROSSING, and only the crossing. `isTrackable` goes
           false the moment status leaves `pending`, so without this the row
           just vanishes and three finished matches are marked by nothing at
           all. Compared against what we PREVIOUSLY held, so an account that
           was already qualified when the rail first read it is not congratulated
           for something it did long ago. */
        const wasPending = String(cached?.status || '') === 'pending';
        cached = result?.invitedBy || null;
        if (wasPending && String(cached?.status || '') === 'qualified') {
          celebrate(cached);
        }
        if (deferMount) return isTrackable(cached);
        const shown = mount(null, { flash });
        /* The rail's own counter has to agree with what is now in the list. */
        try { Quests?.syncSlotCount?.(); } catch (_) { /* rail absent */ }
        return shown;
      })
      .catch(() => false)
      .finally(() => { inFlight = null; });
    return inFlight;
  }

  return {
    mount, refresh, count, relocalize, __setState: (state) => { cached = state; },
  };
}());

export { InviteQuest };
